import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Skip Link component for keyboard navigation
 */
export const SkipLink = ({ href = "#main-content", children = "Skip to main content" }) => (
  <a
    href={href}
    style={{
      position: 'absolute',
      left: '-9999px',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      zIndex: 9999,
      padding: '8px 16px',
      background: '#000',
      color: '#fff',
      textDecoration: 'none',
      borderRadius: '4px',
      fontWeight: 'bold',
      transition: 'all 0.2s ease'
    }}
    onFocus={(e) => {
      e.target.style.left = '10px';
      e.target.style.top = '10px';
      e.target.style.width = 'auto';
      e.target.style.height = 'auto';
    }}
    onBlur={(e) => {
      e.target.style.left = '-9999px';
      e.target.style.width = '1px';
      e.target.style.height = '1px';
    }}
  >
    {children}
  </a>
);

SkipLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node
};

/**
 * Focus trap component for modals and dialogs
 */
export const FocusTrap = ({ children, isActive = true, restoreFocus = true }) => {
  const containerRef = useRef();
  const previousActiveElement = useRef();

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    // Store the previously focused element
    previousActiveElement.current = document.activeElement;

    // Get all focusable elements
    const getFocusableElements = () => {
      return container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
    };

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    // Focus the first element
    const firstFocusable = getFocusableElements()[0];
    if (firstFocusable) {
      firstFocusable.focus();
    }

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      
      // Restore focus to the previously focused element
      if (restoreFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive, restoreFocus]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
};

FocusTrap.propTypes = {
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool,
  restoreFocus: PropTypes.bool
};

/**
 * Screen reader only text component
 */
export const ScreenReaderOnly = ({ children, as: Component = 'span' }) => (
  <Component
    style={{
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0
    }}
  >
    {children}
  </Component>
);

ScreenReaderOnly.propTypes = {
  children: PropTypes.node.isRequired,
  as: PropTypes.elementType
};

/**
 * Announcement component for screen readers
 */
export const Announcement = ({ message, priority = 'polite' }) => {
  const announceRef = useRef();

  useEffect(() => {
    if (message && announceRef.current) {
      announceRef.current.textContent = message;
    }
  }, [message]);

  return (
    <div
      ref={announceRef}
      aria-live={priority}
      aria-atomic="true"
      style={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden'
      }}
    />
  );
};

Announcement.propTypes = {
  message: PropTypes.string,
  priority: PropTypes.oneOf(['polite', 'assertive', 'off'])
};

/**
 * Enhanced button with proper accessibility
 */
export const AccessibleButton = ({
  children,
  onClick,
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
  ariaExpanded,
  ariaControls,
  ariaPressed,
  type = 'button',
  className,
  ...props
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-pressed={ariaPressed}
      className={className}
      style={{
        minHeight: '44px',
        minWidth: '44px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...props.style
      }}
      {...props}
    >
      {children}
    </button>
  );
};

AccessibleButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaControls: PropTypes.string,
  ariaPressed: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string
};

/**
 * Accessible heading with proper hierarchy
 */
export const AccessibleHeading = ({ 
  level = 1, 
  children, 
  id, 
  className,
  ...props 
}) => {
  const HeadingTag = `h${Math.min(Math.max(level, 1), 6)}`;
  
  return (
    <HeadingTag
      id={id}
      className={className}
      {...props}
    >
      {children}
    </HeadingTag>
  );
};

AccessibleHeading.propTypes = {
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  className: PropTypes.string
};

const AccessibilityComponents = {
  SkipLink,
  FocusTrap,
  ScreenReaderOnly,
  Announcement,
  AccessibleButton,
  AccessibleHeading
};

export default AccessibilityComponents;
